import { useChat } from "@ai-sdk/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { DefaultChatTransport, isToolUIPart, type UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, chatFetch } from "../lib/api-client.ts";
import { HTTP } from "../lib/api-error.ts";
import { API_BASE, apiUrl } from "../lib/api-routes.ts";

import { useSession } from "../app/session.tsx";
import {
  addTotals,
  readTurnUsage,
  sumTurns,
  totalTokens,
  type TurnUsage,
  type UsageTotals,
} from "../usage/usage.ts";
import type {
  AgentMessage,
  AgentPart,
  AgentStatus,
  ConfirmationItem,
  ConfirmationResult,
} from "./agent-event.ts";
import { startConversation } from "./attachments.ts";
import {
  loadConversation,
  namedTitle,
  useConversationTitle,
  type LoadedConversation,
} from "./conversations.ts";
import {
  collectOutcomes,
  countPendingDecisions,
  isAwaitingConfirmation,
} from "./decisions.ts";
import { restoreUnsent } from "./draft.ts";

/**
 * ===========================================================================
 * LA COSTURA (seccion 9.1)
 * ===========================================================================
 *
 * Este es el UNICO archivo del frontend que conoce el Vercel AI SDK. Envuelve
 * `useChat` y expone hacia arriba el vocabulario propio (`AgentMessage`,
 * `AgentPart`).
 *
 * A cambio de esta traduccion nos quedamos gratis con el ensamblado del
 * stream, el estado de la conversacion, el aborto, los reintentos y la maquina
 * de estados de las llamadas a herramienta. Y si algun dia el SDK estorba, se
 * reescribe este archivo y ningun componente se entera.
 */

/**
 * Data parts tipadas que emite nuestro backend (`ai-sdk-writer.ts`).
 *
 * Tiene que ser un alias de tipo, no una interface: las interfaces no reciben
 * indice implicito, asi que no satisfacen `UIDataTypes` (= Record<string,
 * unknown>) y el tipo de `data` colapsaria a `unknown`.
 */
type AgentDataParts = {
  conversation: { conversationId: string };
  ui_component: { render: string; props: Record<string, unknown> };
  resource_link: {
    resource: { type: string; id?: string; label: string };
  };
  confirmation_request: {
    actionId: string;
    toolName: string;
    summary: string;
    input: unknown;
    callId?: string;
    items?: ConfirmationItem[];
  };
  confirmation_result: ConfirmationResult;
  question_request: {
    questionId: string;
    prompt: string | null;
    questions: {
      key: string;
      header: string;
      text: string;
      multiSelect: boolean;
      options: { label: string; description: string }[];
    }[];
  };
  file: { path: string; name: string; mimeType: string; sizeBytes: number };
  agent_error: { code: string; message: string };
  turn_finished: {
    conversationId: string;
    messageId: string | null;
    /** Se lee con `readTurnUsage`: la forma crece con el contrato (2.3). */
    usage: unknown;
  };
};

type AgentUIMessage = UIMessage<unknown, AgentDataParts>;

export interface UseAgentChatResult {
  readonly messages: readonly AgentMessage[];
  readonly status: AgentStatus;
  readonly error: Error | undefined;
  readonly conversationId: string | undefined;
  /**
   * Cómo llamar a esta conversación, o `null` si todavía no tiene nombre.
   *
   * `null` no es un error ni un hueco que haya que rellenar: es el estado
   * normal de una conversación recién empezada, porque quien la nombra es un
   * modelo en el backend y tarda un momento en hacerlo. Quien lo pinta tiene
   * que saber DECIR que aún no hay título (ver `UNTITLED_CONVERSATION`).
   */
  readonly title: string | null;
  /** Los turnos antiguos que el agente ya solo ve resumidos, si los hay. */
  readonly compacted:
    { readonly count: number; readonly summary: string } | undefined;
  /** Lo que lleva gastado la conversación abierta, si ha gastado algo. */
  readonly usage: UsageTotals | undefined;
  /** Se está recuperando una conversación del historial. */
  readonly loadingHistory: boolean;
  /**
   * Por qué no se pudo abrir la conversación de la URL, si no se pudo.
   *
   * Antes se tragaba y el hilo quedaba en blanco: una conversación ajena --un
   * 403-- o borrada --un 404-- se leía igual que una vacía.
   */
  readonly historyError: Error | undefined;
  /**
   * Tarjetas que siguen esperando una decisión en esta conversación.
   *
   * El bucle del agente está PARADO hasta que se contesten (§8.2), así que
   * esto no es un adorno: es lo que permite avisar cuando la ventana del chat
   * está cerrada y alguien tiene que decidir.
   */
  readonly pendingDecisions: number;
  /**
   * Qué confirmaciones y preguntas se han contestado YA en esta sesión.
   *
   * Hace falta aquí arriba y no dentro de cada tarjeta: la tarjeta se
   * desmonta al cerrar la ventana del chat o al cambiar de modo, y al volver
   * a montarse ofrecería otra vez unos botones que ya no valen. El backend
   * solo cuenta lo que pasó (`settled`) al releer la conversación.
   */
  readonly answered: ReadonlySet<string>;
  /** Cómo se decidió cada confirmación de esta sesión: `true` es aprobar. */
  readonly decisions: ReadonlyMap<string, boolean>;
  /**
   * Cómo acabó cada tarjeta según el backend (R2.1), por su `actionId`.
   * Sale del historial y del stream: es lo que dice "7 de 8 hechas".
   */
  readonly outcomes: ReadonlyMap<string, ConfirmationResult>;
  send: (text: string) => void;
  /**
   * Empieza una conversación NUEVA con esta pregunta, en una sola operación.
   *
   * No es `startNew()` y luego `send()`: `send` es un `useCallback` cerrado
   * sobre el `conversationId` de este render, así que encadenarlos en el mismo
   * tick mandaría el mensaje a la conversación ANTERIOR --y encima sin que se
   * note, porque en pantalla todo parece correcto--. Aquí el cuerpo va vacío a
   * propósito: sin `conversationId`, el backend crea una.
   */
  askInNewConversation: (text: string) => void;
  /** Responde a una tarjeta de confirmación y continúa la conversación. */
  resolveConfirmation: (actionId: string, approved: boolean) => void;
  answerQuestions: (
    questionId: string,
    answers: readonly {
      key: string;
      selected: readonly string[];
      custom?: string;
    }[],
    note?: string,
  ) => void;
  stop: () => void;
  clearError: () => void;
  /** Crea la conversación si aún no existe. La necesitan los adjuntos. */
  ensureConversation: () => Promise<string>;
  /** Abre una conversación del historial. */
  open: (conversationId: string) => void;
  /** Empieza de cero. La anterior no se pierde: está en el historial. */
  startNew: () => void;
}

/**
 * La conversación abierta viaja en la URL, en el parámetro `c`.
 *
 * Es lo que hace que recargar no pierda el hilo y que se pueda compartir un
 * enlace a un chat. Lo escribe el router --que además valida que sea un uuid--
 * en vez de manejarse con `history.replaceState`.
 */

export const useAgentChat = (): UseAgentChatResult => {
  const navigate = useNavigate();
  // `list_conversations`/`get_conversation` son tools `internal: true`: el
  // backend no infiere el usuario de la sesión en REST directo, hay que
  // mandarlo explícito.
  const userId = useSession().me?.userId ?? "";
  /**
   * La conversación abierta la manda la URL, no un estado paralelo.
   *
   * Con dos fuentes --una en la URL y otra en el componente-- tarde o temprano
   * se separan, y entonces el botón de atrás del navegador enseña una cosa y
   * la aplicación otra.
   */
  const conversationId = useSearch({
    from: "__root__",
    select: (search) => search.c,
  });

  const setConversationId = useCallback(
    (id: string | undefined) => {
      void navigate({
        to: ".",
        search: (previous) => ({ ...previous, c: id }),
        replace: true,
      });
    },
    [navigate],
  );
  /**
   * Lo recuperado del historial, aparte de lo que lleva el SDK.
   *
   * No se le inyecta al SDK: lo suyo es el turno en curso. Mezclarlos aquí,
   * al leer, evita traducir nuestro vocabulario al suyo para volver a
   * traducirlo de vuelta en cuanto se dibuja.
   */
  const [history, setHistory] = useState<LoadedConversation | undefined>();
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<Error | undefined>();
  /** Ids de confirmaciones y preguntas contestadas en esta sesión. */
  const [answered, setAnswered] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /**
   * Qué se decidió en cada confirmación de esta sesión: aprobar o rechazar.
   *
   * `answered` dice que ya no espera; esto dice cómo acabó, y lo necesitan
   * las llamadas a herramienta del mismo mensaje para dejar de decir "espera
   * tu confirmación" y decir lo que pasó. Al recargar se pierde: el backend
   * solo cuenta si sigue pendiente.
   */
  const [decisions, setDecisions] = useState<ReadonlyMap<string, boolean>>(
    () => new Map(),
  );

  const markAnswered = useCallback((id: string) => {
    setAnswered((current) => new Set(current).add(id));
  }, []);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<AgentUIMessage>({
        api: apiUrl(`${API_BASE}/chat`),
        fetch: chatFetch,
        // El backend espera { text, conversationId }, no el array de mensajes
        // del SDK: el historial ya vive en Postgres y se carga alli, que es la
        // unica fuente de verdad de la conversacion.
        prepareSendMessagesRequest: ({ messages, body }) => {
          const last = messages.at(-1);
          const text =
            last?.parts
              .filter((part) => part.type === "text")
              .map((part) => part.text)
              .join("") ?? "";

          // Ni una confirmación ni unas respuestas llevan texto: van en el
          // body y el backend las reconoce por ahí.
          const sent = body as
            { resolve?: unknown; answer?: unknown } | undefined;
          const isReply =
            sent?.resolve !== undefined || sent?.answer !== undefined;
          return { body: isReply ? { ...body } : { ...body, text } };
        },
      }),
    [],
  );

  /**
   * Lo último que se mandó, por si hay que devolverlo a la caja.
   *
   * Si la sesión caduca y no se puede renovar, el turno falla con un 401 y la
   * aplicación vuelve al SSO. El mensaje ya no está en el compositor --se
   * vació al enviar-- y sin esto se perdería en el viaje.
   */
  const lastSent = useRef("");

  const chat = useChat<AgentUIMessage>({
    transport,
    onError: (error) => {
      if (error instanceof ApiError && error.status === HTTP.unauthorized) {
        restoreUnsent(lastSent.current);
      }
    },
    // Sin esto, cada fragmento del stream provoca un render del hilo entero.
    // 60 ms es imperceptible al leer y corta la mayor parte del trabajo.
    throttle: 60,
    onData: (part) => {
      // El id de la conversacion llega al inicio del primer turno. Se escribe
      // en la URL en cuanto se sabe: si el usuario recarga a mitad del turno,
      // vuelve a este chat y no a uno en blanco.
      if (part.type === "data-conversation") {
        setConversationId(part.data.conversationId);
      }
    },
  });

  const send = useCallback(
    (text: string) => {
      const clean = text.trim();
      if (clean === "") return;
      lastSent.current = clean;
      void chat.sendMessage(
        { text: clean },
        { body: conversationId === undefined ? {} : { conversationId } },
      );
    },
    [chat, conversationId],
  );

  const resolveConfirmation = useCallback(
    (actionId: string, approved: boolean) => {
      markAnswered(actionId);
      setDecisions((current) => new Map(current).set(actionId, approved));
      void chat.sendMessage(
        // Sin texto: lo que el usuario "dice" es su decisión, y el backend la
        // convierte en el mensaje que el modelo leerá.
        { text: "" },
        {
          body: {
            resolve: { actionId, approved },
            ...(conversationId === undefined ? {} : { conversationId }),
          },
        },
      );
    },
    [chat, conversationId, markAnswered],
  );

  const answerQuestions = useCallback(
    (
      questionId: string,
      answers: readonly {
        key: string;
        selected: readonly string[];
        custom?: string;
      }[],
      note?: string,
    ) => {
      markAnswered(questionId);
      void chat.sendMessage(
        { text: "" },
        {
          body: {
            answer: {
              questionId,
              answers,
              ...(note !== undefined && note.trim() !== ""
                ? { note: note.trim() }
                : {}),
            },
            ...(conversationId === undefined ? {} : { conversationId }),
          },
        },
      );
    },
    [chat, conversationId, markAnswered],
  );

  const { setMessages } = chat;

  const open = useCallback(
    (id: string) => {
      setLoadingHistory(true);
      setHistoryError(undefined);
      setConversationId(id);

      setMessages([]);
      setAnswered(new Set());
      setDecisions(new Map());

      loadConversation(id, userId)
        .then(setHistory)
        .catch((cause: unknown) => {
          // Una conversación que no se puede abrir no bloquea el chat: se
          // deja en blanco y el usuario puede escribir igual. Pero se dice
          // por qué, en vez de hacer pasar el fallo por un hilo vacío.
          setHistory(undefined);
          setHistoryError(
            cause instanceof Error ? cause : new Error(String(cause)),
          );
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    },
    [setConversationId, setMessages, userId],
  );

  const startNew = useCallback(() => {
    setHistoryError(undefined);
    setConversationId(undefined);
    setHistory(undefined);
    setMessages([]);
    setAnswered(new Set());
    setDecisions(new Map());
  }, [setConversationId, setMessages]);

  /**
   * Preguntar algo en una conversación nueva, de una sola vez.
   *
   * Lo usan las alertas del Resumen: cada una es una investigación suya
   * --"¿quiénes son los 18 que se quedaron fuera?"-- y colarla en un hilo que
   * iba de otra cosa ensucia el contexto que ve el modelo, el título que le
   * pone el backend y lo que la extracción de memoria (§11) saque después.
   *
   * Lo importante es que el envío va en la MISMA llamada que la limpieza, con
   * el cuerpo vacío. Partirlo en `startNew()` + `send()` colgaría el mensaje
   * de la conversación anterior, porque `send` lleva dentro el id de este
   * render.
   */
  const askInNewConversation = useCallback(
    (text: string) => {
      const clean = text.trim();
      if (clean === "") return;
      lastSent.current = clean;

      setConversationId(undefined);
      setHistory(undefined);
      setHistoryError(undefined);
      setMessages([]);
      setAnswered(new Set());
      setDecisions(new Map());

      // Sin `conversationId` en el cuerpo: es lo que le dice al backend que
      // cree una. El id de la nueva llega por el stream y se escribe en la URL.
      void chat.sendMessage({ text: clean }, { body: {} });
    },
    [chat, setConversationId, setMessages],
  );

  // Al abrir con ?c=<id>, se recupera esa conversación. Es lo que hace que
  // recargar a mitad de un chat no lo pierda.
  useEffect(() => {
    if (conversationId !== undefined) open(conversationId);
    // Solo al montar: después, quien cambia la URL es `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId !== undefined) return conversationId;
    const created = await startConversation();
    setConversationId(created);
    return created;
  }, [conversationId, setConversationId]);

  const live = useMemo(
    () => chat.messages.map(toAgentMessage),
    [chat.messages],
  );

  const messages = useMemo(
    () => [...(history?.messages ?? []), ...live],
    [history, live],
  );

  /**
   * Lo que lleva gastado la conversación abierta.
   *
   * Lo recuperado lo dice el backend --y cuenta también lo que corre en el
   * worker para esta conversación: títulos, compactación, memoria--; lo de
   * esta sesión se suma turno a turno según llega, sin volver a pedir la
   * conversación entera por un número. Al recargar vuelve a mandar el backend.
   */
  const usage = useMemo(() => {
    const recovered =
      history?.usage ?? sumTurns((history?.messages ?? []).map((m) => m.usage));
    const total = addTotals(recovered, sumTurns(live.map((m) => m.usage)));
    return totalTokens(total) === 0 ? undefined : total;
  }, [history, live]);

  /**
   * El título sale del historial, no del hilo que se está leyendo.
   *
   * Aquí hubo una segunda vía: recortar el primer mensaje del usuario para que
   * la cabecera no estuviera vacía durante el primer turno, porque el backend
   * derivaba el título al guardar y no llegaba por el stream. Ya no hace
   * falta: ahora lo escribe un modelo, y un recorte puesto por delante taparía
   * justo el momento que se quiere enseñar --el de "sin título" convirtiéndose
   * en el de verdad--.
   *
   * La lista manda sobre lo cargado al abrir: `history` se lee una sola vez y
   * se queda con el título que hubiera entonces, que en una conversación nueva
   * es ninguno.
   */
  const listed = useConversationTitle(conversationId, userId);
  const title = listed ?? namedTitle(history?.title);

  const outcomes = useMemo(() => collectOutcomes(messages), [messages]);

  /** La regla vive en `decisions.ts`, que se puede probar sin montar un chat. */
  const pendingDecisions = useMemo(
    () => countPendingDecisions(messages, answered),
    [messages, answered],
  );

  const compacted = useMemo(
    () =>
      history?.summary === null || history?.summary === undefined
        ? undefined
        : { count: history.compactedCount, summary: history.summary },
    [history],
  );

  return {
    messages,
    status: toAgentStatus(chat.status),
    error: chat.error,
    conversationId,
    title,
    compacted,
    usage,
    loadingHistory,
    historyError,
    pendingDecisions,
    answered,
    decisions,
    outcomes,
    send,
    askInNewConversation,
    resolveConfirmation,
    answerQuestions,
    // `stop` del SDK devuelve una promesa; se envuelve para que los
    // componentes reciban un handler sincrono y no tengan que saberlo.
    stop: () => {
      void chat.stop();
    },
    clearError: chat.clearError,
    ensureConversation,
    open,
    startNew,
  };
};

const toAgentStatus = (status: string): AgentStatus => {
  switch (status) {
    case "submitted":
      return "submitted";
    case "streaming":
      return "streaming";
    case "error":
      return "error";
    default:
      return "idle";
  }
};

/** Traduccion de una UIMessage del SDK a nuestro vocabulario. */
const toAgentMessage = (message: AgentUIMessage): AgentMessage => {
  const parts: AgentPart[] = [];
  const results: ConfirmationResult[] = [];
  let usage: TurnUsage | undefined;

  for (const part of message.parts) {
    if (part.type === "text") {
      // Aprobar o contestar se envía sin texto: lo que "dice" el usuario es
      // su decisión, y ya la enseña la tarjeta. Un texto vacío no es nada.
      if (part.text !== "") parts.push({ type: "text", text: part.text });
      continue;
    }

    if (part.type === "reasoning") {
      parts.push({ type: "reasoning", text: part.text });
      continue;
    }

    if (isToolUIPart(part)) {
      parts.push(toToolPart(part));
      continue;
    }

    if (part.type === "data-ui_component") {
      parts.push({
        type: "ui_component",
        render: part.data.render,
        props: part.data.props,
      });
      continue;
    }

    if (part.type === "data-resource_link") {
      parts.push({ type: "resource_link", resource: part.data.resource });
      continue;
    }

    if (part.type === "data-question_request") {
      parts.push({ type: "question_request", ...part.data });
    }

    if (part.type === "data-confirmation_request") {
      parts.push({ type: "confirmation_request", ...part.data });
      continue;
    }

    // Cómo acabó una tarjeta (R2.1): llega al empezar el turno que arranca
    // la decisión. No se dibuja aquí; lo leen la tarjeta y sus llamadas.
    if (part.type === "data-confirmation_result") {
      results.push(part.data);
      continue;
    }

    if (part.type === "data-file") {
      parts.push({ type: "file", ...part.data });
      continue;
    }

    if (part.type === "data-agent_error") {
      parts.push({ type: "error", ...part.data });
      continue;
    }
    // El fin del turno no se dibuja como una parte: trae lo que costó, y eso
    // va al pie del mensaje.
    if (part.type === "data-turn_finished") {
      usage = readTurnUsage(part.data.usage);
      continue;
    }
    // data-conversation es una senal de control: no se dibuja.
  }

  return {
    id: message.id,
    role: message.role === "system" ? "system" : message.role,
    parts,
    ...(usage !== undefined ? { usage } : {}),
    ...(results.length > 0 ? { confirmationResults: results } : {}),
  };
};

interface ToolLikePart {
  readonly type: string;
  readonly toolCallId: string;
  readonly state: string;
  readonly input?: unknown;
  readonly output?: unknown;
  readonly errorText?: string;
}

const toToolPart = (part: unknown): AgentPart => {
  const tool = part as ToolLikePart;
  const toolName = tool.type.startsWith("tool-")
    ? tool.type.slice("tool-".length)
    : tool.type;

  const state: "running" | "done" | "error" | "awaiting" =
    tool.state === "output-available"
      ? isAwaitingConfirmation(tool.output)
        ? "awaiting"
        : "done"
      : tool.state === "output-error"
        ? "error"
        : "running";

  return {
    type: "tool",
    callId: tool.toolCallId,
    toolName,
    state,
    ...(tool.input !== undefined ? { input: tool.input } : {}),
    ...(tool.output !== undefined ? { output: tool.output } : {}),
    ...(tool.errorText !== undefined ? { errorText: tool.errorText } : {}),
  };
};
